// Stage 5's code rules, copied from production (trackable-api
// src/lib/langgraph/stages/validation.ts): merge the stages' answers per person, clean
// phones and dates, make outcomes consistent, fix patient types, and drop duplicates.
// Both eval pipelines end here, so a score difference between them comes from the
// prompts, not from these rules.

import type { FinalPerson, StageResults } from './types'

function cleanPhone(phone?: string): string | undefined {
  if (!phone) return undefined
  const digits = phone.replace(/\D/g, ``)
  if (digits.length < 10) return undefined
  return digits.slice(-10)
}

function normalizeDate(value?: string): string | undefined {
  if (!value) return undefined
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value
  const date = new Date(value)
  if (isNaN(date.getTime())) return undefined
  return date.toISOString().split(`T`)[0]
}

function normalizeTime(value?: string): string | undefined {
  if (!value) return undefined
  if (/^\d{1,2}:\d{2}\s*(am|pm)$/i.test(value)) return value.toLowerCase()
  return value
}

/** People with the same name and relationship are one person; keep the earliest booking. */
function deduplicatePeople(people: FinalPerson[]): FinalPerson[] {
  const groups = new Map<string, FinalPerson[]>()
  for (const person of people) {
    const name = (person.name || `unknown`).toLowerCase()
    const relation = person.relationship?.toLowerCase() || (person.isCaller ? `caller` : ``)
    const key = `${name}|${relation}`
    groups.set(key, [...(groups.get(key) ?? []), person])
  }
  return [...groups.values()].map((group) => {
    const booked = group.filter((person) => person.appointmentDate)
    if (group.length === 1 || booked.length === 0) return group[0]
    return [...booked].sort((a, b) => (a.appointmentDate! > b.appointmentDate! ? 1 : -1))[0]
  })
}

function validateOutcomes(person: FinalPerson): string[] {
  let validated = [...new Set((person.outcomes ?? []).map((outcome) => outcome.trim()))]

  const hasScheduled = validated.includes(`Appointment Scheduled`)
  const hasRescheduled = validated.includes(`Appointment Rescheduled`)
  if (hasScheduled && hasRescheduled) validated = validated.filter((o) => o !== `Appointment Scheduled`)
  if (person.appointmentScheduled && !hasScheduled && !hasRescheduled) validated.push(`Appointment Scheduled`)

  if (person.appointmentRequested && !person.appointmentScheduled) {
    if (person.reasonForNotScheduling?.toLowerCase().includes(`cancel`)) {
      validated = validated.filter((o) => o !== `Appointment Not Scheduled`)
      if (!validated.includes(`Canceled Appointment`)) validated.push(`Canceled Appointment`)
    } else if (!validated.includes(`Appointment Not Scheduled`) && !validated.includes(`Canceled Appointment`)) {
      validated.push(`Appointment Not Scheduled`)
    }
  }

  if (validated.length > 1 && validated.includes(`Missed Call`))
    validated = validated.filter((o) => o !== `Missed Call`)
  if (!person.isCaller && validated.includes(`Voicemail`)) validated = validated.filter((o) => o !== `Voicemail`)

  const meaningful = validated.filter((o) => o !== `Other` && o !== `Missed Call`)
  if (meaningful.length > 0 && validated.includes(`Other`)) validated = validated.filter((o) => o !== `Other`)

  return [...new Set(validated)]
}

/** Stage 5's merge and rules, before the action-items call. */
export function assemblePeople(results: StageResults): FinalPerson[] {
  const { overview, people = [], appointmentData, outcomeData } = results

  const assembled = people.map((person, i) => {
    const appt = appointmentData?.find((entry) => entry.personIndex === i)
    const outcome = outcomeData?.find((entry) => entry.personIndex === i)
    const merged: FinalPerson = {
      name: person.name,
      phone: cleanPhone(person.phone),
      email: person.email,
      birthDate: normalizeDate(person.birthDate),
      appointmentDate: normalizeDate(appt?.appointmentDate),
      appointmentTime: normalizeTime(appt?.appointmentTime),
      appointmentScheduled: appt?.appointmentScheduled,
      appointmentRequested: appt?.appointmentRequested,
      treatment: appt?.treatment || undefined,
      serviceRequested: appt?.serviceRequested,
      outcomes: outcome?.outcomes,
      patientType: person.patientType,
      relationship: person.relationship,
      referral: outcome?.referral || overview?.channel,
      insuranceProvider: outcome?.insuranceProvider,
      isCaller: person.isCaller,
      reasonForNotScheduling: appt?.reasonForNotScheduling,
    }
    merged.outcomes = validateOutcomes(merged)

    if (merged.appointmentScheduled && (merged.patientType === `Non-Patient` || merged.patientType === `Other`)) {
      merged.patientType = `Other`
    } else if (merged.appointmentRequested && merged.patientType === `Non-Patient`) {
      merged.patientType = `New Patient`
    }
    return merged
  })

  const siblingTypes = assembled
    .filter((p) => !p.isCaller && p.patientType !== `Other` && p.patientType !== `Non-Patient`)
    .map((p) => p.patientType)
  const dominantType = siblingTypes[0] ?? `New Patient`

  for (const person of assembled) {
    if (person.isCaller && !person.appointmentScheduled) {
      if (person.patientType !== `New Patient`) person.patientType = `Non-Patient`
      const onlyOther = person.outcomes?.length === 1 && person.outcomes[0] === `Other`
      if ((!person.outcomes?.length || onlyOther) && !person.outcomes?.includes(`Voicemail`)) {
        person.outcomes = [`Other`]
      }
    } else if (!person.isCaller && person.appointmentScheduled && person.patientType === `Other`) {
      person.patientType = dominantType
      person.outcomes = validateOutcomes(person)
    }
  }

  return deduplicatePeople(assembled)
}

/** Production asks the model for action items only when an outcome needs follow-up. */
export const needsActionItems = (people: FinalPerson[]) =>
  people.some((p) => [`Appointment Not Scheduled`, `Callback`, `Voicemail`].some((o) => p.outcomes?.includes(o)))
